import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Input, Modal } from "reactstrap";
import {
  listofDeliveryBoy,
  AssignDeliveryBoy,
  ReAssignDeliveryBoy,
  OrderListbyId,
  OrderFullfilled,
  OrderList,
  updatepaymentstatus,
  cancelFoodOrder,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { AvForm } from "availity-reactstrap-validation";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Btn, Chip, ConfirmDialog, PillToggle, SearchPill, Table,
} from "../../components/Console/ConsoleUI";
import PeriodFilter, { allTime, periodToParams } from "../../components/Common/PeriodFilter";

/**
 * Food orders — screens B2 (live board) and B3 (order detail) of the console
 * redesign.
 *
 * The board groups the page of orders the API returns into the four states a
 * kitchen actually works in, so the next thing to do is visible without reading
 * a status column. A list view is kept beside it for scanning a busy service,
 * and both are the same page of the same request — paging and search are
 * unchanged.
 *
 * Re-skin only: every request, state field and handler below is the one the
 * previous table screen used.
 */

const CSS = `
.order-modal .modal-content{border:0;border-radius:22px;background:#fdf9f3;
  box-shadow:0 24px 60px rgba(0,0,0,.35);overflow:hidden}
.order-card{background:#fff;border:1px solid ${C.tint};border-radius:14px;padding:15px;
  cursor:pointer;transition:box-shadow .15s,border-color .15s}
.order-card:hover{border-color:${C.sand};box-shadow:0 8px 22px rgba(61,40,23,.09)}
.order-mini{border:0;background:transparent;font-family:inherit;font-size:13px;padding:0;
  cursor:pointer;height:34px;border-radius:17px;padding:0 15px}
.order-page{height:32px;min-width:32px;padding:0 10px;border-radius:16px;font-size:13px;
  font-family:inherit;cursor:pointer;border:1px solid ${C.border};background:#fff;color:${C.slate}}
.order-page:hover:not(:disabled){border-color:${C.brown};color:${C.brown}}
.order-page:disabled{opacity:.45;cursor:default}
.order-page.is-active{background:${C.brown};border-color:${C.brown};color:#fff}
.order-modal .form-control,.order-modal .custom-select{height:48px;padding:0 15px;background:#fff;
  border:1px solid #e6d8c4;border-radius:12px;font-size:15px;color:${C.ink};box-shadow:none}
.order-modal .form-group{margin-bottom:0}
`;

/** The board's four lanes, in the order a plate moves through them. */
const LANES = [
  { key: "new", label: "New", dot: "#b26a00" },
  { key: "accepted", label: "Accepted · in the kitchen", dot: C.brown },
  { key: "out", label: "Out for delivery", dot: C.green },
  { key: "closed", label: "Closed", dot: "#bfae99" },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Order timestamps come back preformatted on some records and as ISO strings on
 * others, so anything unparseable is shown exactly as the API sent it rather
 * than as "Invalid Date".
 */
const when = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const orderRef = (o) =>
  (o && o.orderNumber) ||
  (o && o._id ? `#${String(o._id).slice(-6).toUpperCase()}` : "—");

/**
 * The untouched order document. /foodOrderList flattens a handful of fields
 * onto each row and ships the full record beside them as `item`; newer fields
 * (statusHistory, charges, points) are only on the latter.
 */
const rawOf = (o) => (o && o.item) || {};

/**
 * When the order was placed. The row's own `createdAt` is preformatted
 * "D-MM-YYYY, hh:mm AM", which Date reads month-first (1 Nov became 11 Jan),
 * so the raw ISO timestamp is preferred.
 */
const placedAt = (o) => rawOf(o).createdAt || (o && o.createdAt);

/** Human labels for OrderFood.statusHistory[].status. */
const HISTORY_LABELS = {
  placed: "Placed",
  paid: "Payment received",
  accepted: "Accepted by kitchen",
  assigned: "Runner assigned",
  out_for_delivery: "Out for delivery",
  on_the_way: "On the way",
  delivered: "Delivered",
  rejected: "Declined by runner",
  cancelled: "Cancelled",
  delivery_failed: "Delivery failed",
};

/** Timeline entries drawn in red. */
const BAD_HISTORY = ["rejected", "cancelled", "delivery_failed"];

/**
 * A field from the flattened row, else from the raw order. /foodOrderList only
 * flattens the older flags; the delivery-failure, cancelled-by and runner-log
 * fields are on `item` alone. Null-safe on orders that predate them.
 */
const fieldOf = (o, k) => (o && o[k] !== undefined && o[k] !== null ? o[k] : rawOf(o)[k]);

/** A ref that may arrive as a bare id or a populated document. */
const idOf = (v) => (v && typeof v === "object" ? String(v._id || "") : String(v || ""));

const isFailed = (o) => !!fieldOf(o, "isDeliveryFailed");

/** Why a runner couldn't deliver (OrderFood.deliveryFailedReason). */
const FAILED_REASONS = {
  customer_unreachable: "Guest unreachable",
  wrong_address: "Wrong address",
  customer_refused: "Guest refused it",
  cannot_access: "Couldn't access the address",
  vehicle_problem: "Vehicle problem",
  other: "Other",
};

const failedReasonLabel = (r) =>
  FAILED_REASONS[r] || (r ? String(r).replace(/_/g, " ") : "No reason given");

/** OrderFood.driverLog[].event, as the runner history reads it. */
const DRIVER_EVENTS = {
  rejected: "Declined the order",
  failed: "Couldn't deliver",
  reassigned_away: "Taken off the order",
  cancelled: "Order cancelled while with them",
};

/** "Cancelled by the guest" / "by the hotel"; blank on orders before cancelledBy. */
const cancelledByLabel = (o) => {
  const by = fieldOf(o, "cancelledBy");
  if (by === "admin") return "Cancelled by the hotel";
  if (by === "guest") return "Cancelled by the guest";
  return "";
};

/** Cancellable by the hotel: what cancelFoodOrderByAdmin accepts. */
const canCancel = (o) => !!o && !o.isCancelled && !o.isDelivered;

/**
 * Assign vs reassign. AssignDeliveryBoy only takes an order nobody has had;
 * anything a runner declined, failed or still holds goes to ReAssignDeliveryBoy.
 */
const assignModeOf = (r) =>
  r && r.isRejected === false && !r.isDeliveryFailed && !r.isDeliveryBoyAssigned && !r.isOrderAccpeted
    ? "assign"
    : "reassign";

/** The text box used for the optional reassign / cancel reasons. */
const reasonStyle = {
  width: "100%", minHeight: 76, height: "auto", padding: "11px 14px", borderRadius: 12,
  border: "1px solid #e6d8c4", background: "#fff", fontSize: 14, color: C.ink,
  fontFamily: "inherit", resize: "vertical", boxShadow: "none",
};

const pts = (n) => `${Number(n || 0).toLocaleString()} pts`;

/** Pill row across the food family - see food/index.js. */
const NavPills = ({ items, active }) => (
  <div style={{ display: "flex", padding: 4, borderRadius: 21, background: "#efe2ce" }}>
    {items.map((i) => (
      <Link
        key={i.to}
        to={i.to}
        style={{
          padding: "8px 16px", borderRadius: 17, fontSize: 14, textDecoration: "none",
          whiteSpace: "nowrap",
          background: i.to === active ? C.brown : "transparent",
          color: i.to === active ? "#fff" : C.taupe,
        }}
      >
        {i.label}
      </Link>
    ))}
  </div>
);


class FoodOrder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      DBoyData: [],
      isLoaded: false,
      modal: false,
      Dboy: "",
      foodorderId: "",
      isRejected: "",
      // Assign / reassign: the order as last read, which call to make, and
      // the optional reason sent with a reassign.
      assignOrder: null,
      assignMode: "assign",
      reassignReason: "",
      // Hotel cancellation (ConfirmDialog)
      cancelTarget: null,
      cancelReason: "",
      cancelling: false,
      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      limit: 15,
      search: "",
      period: allTime(),
      // Order detail modal
      showOrderDetail: false,
      selectedOrder: null,
      // Presentation only — which arrangement of the same page is shown.
      view: "board",
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Food Orders", link: "#" },
      ],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggle = this.toggle.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
  }

  toggle() {
    this.setState({
      modal: !this.state.modal,
      foodorderId: "",
      Dboy: "",
      isRejected: "",
      assignOrder: null,
      assignMode: "assign",
      reassignReason: "",
    });
  }

  handleCloseModal() {
    this.setState({ modal: false });
  }

  handleChange = (e) => {
    this.setState({ [e.target.id]: e.target.value });
  };

  listDeliveryBoy() {
    listofDeliveryBoy()
      .then((result) => {
        this.setState({ DBoyData: result?.data || [] });
      })
      .catch((error) => console.error(error));
  }

  getALllorder(page = this.state.currentPage, search = this.state.search) {
    const { limit } = this.state;
    this.setState({ isLoaded: false });

    OrderList(page, limit, search, periodToParams(this.state.period))
      .then((result) => {
        const pagination = result?.pagination || {};
        this.setState({
          isLoaded: true,
          items: result?.data || [],
          currentPage: pagination.currentPage || 1,
          totalPages: pagination.totalPages || 1,
          totalCount: pagination.totalCount || 0,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({ isLoaded: true, items: [] });
      });
  }

  // View order detail
  viewOrderDetail = (order) => {
    this.setState({
      showOrderDetail: true,
      selectedOrder: order,
    });
  };

  closeOrderDetail = () => {
    this.setState({
      showOrderDetail: false,
      selectedOrder: null,
    });
  };

  // Get order status badge
  getStatusBadge = (order) => {
    if (order.isCancelled) return <Chip tone="bad">Cancelled</Chip>;
    if (order.isDelivered) return <Chip tone="good">Delivered</Chip>;
    // A failed delivery keeps isDeliveryBoyAssigned, so it is checked first or
    // it would still read "Out for Delivery".
    if (isFailed(order)) return this.failedChip(order);
    if (order.isDeliveryBoyAssigned) return <Chip tone="neutral">Out for Delivery</Chip>;
    if (order.isOrderAccpeted) return <Chip tone="neutral">Accepted</Chip>;
    if (order.isOrderPlaced) return <Chip tone="warn">Placed</Chip>;
    return <Chip tone="warn">Pending</Chip>;
  };

  // Get payment badge
  getPaymentBadge = (order) => {
    // Says "Payment pending", not just "Pending": beside a fulfilment chip
    // reading "Delivered", a bare "Pending" looks like a contradiction.
    if (order.isPaymentSuccess) return <Chip tone="good">Paid</Chip>;
    return <Chip tone="warn">Payment pending</Chip>;
  };

  // Get order type label
  getOrderTypeLabel = (type) => {
    switch (type) {
      case 1: return "Room Service";
      case 2: return "Dine-in";
      case 3: return "Pickup";
      case 4: return "Delivery";
      default: return "N/A";
    }
  };

  /**
   * Open the runner picker for an order. The row is enough to open it at once
   * (newer fields from rawOf); the fresh read then settles assign vs reassign,
   * since a runner may have declined or failed since the board loaded.
   */
  OrderManagement(order) {
    const fromRow = {
      ...rawOf(order),
      _id: order._id,
      isRejected: order.isRejected,
      isDeliveryBoyAssigned: order.isDeliveryBoyAssigned,
      isOrderAccpeted: order.isOrderAccpeted,
      isDeliveryFailed: isFailed(order),
    };
    this.setState({
      modal: true,
      foodorderId: order._id,
      Dboy: "",
      isRejected: order.isRejected,
      assignOrder: fromRow,
      assignMode: assignModeOf(fromRow),
      reassignReason: "",
    });
    OrderListbyId(order._id)
      .then((results) => {
        const fresh = results && results.data;
        if (!fresh) return;
        this.setState({
          foodorderId: fresh._id,
          isRejected: fresh.isRejected,
          assignOrder: fresh,
          assignMode: assignModeOf(fresh),
        });
      })
      .catch(() => toast.error("Error fetching order"));
  }

  OrderFullFill(id) {
    OrderFullfilled(id)
      .then((results) => {
        if (results.status === 200) {
          toast.success(results.message);
          this.getALllorder();
        } else {
          toast.error(results.message);
        }
      })
      .catch(() => toast.error("Error completing order"));
  }

  StatusChangeRequest(id, isPaymentSuccess) {
    const data = { orderId: id, isPaymentSuccess };
    updatepaymentstatus(data)
      .then((response) => {
        if (response.status === 200) {
          toast.success(response.message);
        } else {
          toast.error(response.message);
        }
        this.getALllorder();
      })
      .catch(() => toast.error("Error updating payment status"));
  }

  handleSubmit(e) {
    e.preventDefault();
    const Data = {
      DboyuserId: this.state.Dboy,
      foodorderId: this.state.foodorderId,
    };

    // Reassign takes an optional reason; it is logged against the runner the
    // order is taken from.
    const reassign = this.state.assignMode === "reassign";
    const reason = (this.state.reassignReason || "").trim();
    if (reassign && reason) Data.reason = reason;

    const apiCall = reassign ? ReAssignDeliveryBoy : AssignDeliveryBoy;

    apiCall(Data)
      .then((response) => {
        if (response && response.status === 200 && response.success !== false) {
          toast.success(response.message);
          this.getALllorder();
          this.listDeliveryBoy();
          this.handleCloseModal();
        } else if (response && (response.status === 409 || response.status === 404)) {
          // The order moved on (or left this branch's view) while the picker
          // was open: show the board as it is now rather than retrying blind.
          toast.error(response.message);
          this.getALllorder();
          this.listDeliveryBoy();
          this.handleCloseModal();
        } else {
          toast.error((response && response.message) || "Error assigning delivery boy");
        }
      })
      .catch(() => toast.error("Error assigning delivery boy"));
  }

  // --- hotel cancellation ---------------------------------------------------

  openCancel = (order) => {
    this.closeOrderDetail();
    this.setState({ cancelTarget: order, cancelReason: "", cancelling: false });
  };

  closeCancel = () => {
    if (this.state.cancelling) return;
    this.setState({ cancelTarget: null, cancelReason: "" });
  };

  confirmCancel = () => {
    const { cancelTarget, cancelReason } = this.state;
    if (!cancelTarget) return;
    this.setState({ cancelling: true });
    cancelFoodOrder(cancelTarget._id, cancelReason.trim() || undefined).then((res) => {
      this.setState({ cancelling: false });
      if (res && res.status === 200) {
        toast.success(res.message || "Order cancelled");
        this.setState({ cancelTarget: null, cancelReason: "" });
        this.getALllorder();
        this.listDeliveryBoy();
        return;
      }
      toast.error((res && res.message) || "Couldn't cancel the order");
      // 404 = delivered or cancelled meanwhile, 409 = changed mid-write:
      // either way the board is stale.
      if (res && (res.status === 404 || res.status === 409)) {
        this.setState({ cancelTarget: null, cancelReason: "" });
        this.getALllorder();
      }
    });
  };

  /** A runner's name from the loaded runner list, or "" when not in it. */
  runnerName = (id) => {
    const key = idOf(id);
    if (!key) return "";
    const d = (this.state.DBoyData || []).find((b) => String(b._id) === key);
    return d ? `${d.firstName || ""} ${d.lastName || ""}`.trim() : "";
  };

  /** "Patrick · 2 active" / "Patrick · free". Busy runners stay pickable. */
  runnerLabel = (d) => {
    const name = `${d.firstName || ""} ${d.lastName || ""}`.trim() || "Runner";
    if (typeof d.activeCount !== "number") return name;
    return `${name} · ${d.activeCount > 0 ? `${d.activeCount} active` : "free"}`;
  };

  /** A bad-tone chip for a delivery the runner couldn't complete. */
  failedChip = (order) => (
    <Chip tone="bad">Delivery failed: {failedReasonLabel(fieldOf(order, "deliveryFailedReason"))}</Chip>
  );

  componentDidMount() {
    this.getALllorder();
    this.listDeliveryBoy();
  }

  // Render action buttons for an order
  renderActionButton = (order) => {
    if (order.isCancelled) return <span style={{ color: C.taupeLight }}>-</span>;

    if (order.orderPlacetype === 4) {
      // Delivery orders
      if (order.isDelivered) return <span style={{ color: "#2f7d33", fontSize: 13 }}>Completed</span>;
      // Unassigned, declined or failed: the order needs a runner.
      if (!order.isDeliveryBoyAssigned || isFailed(order)) {
        return (
          <button
            type="button"
            className="order-mini"
            style={{ background: C.brown, color: "#fff" }}
            onClick={(e) => {
              e.stopPropagation();
              this.OrderManagement(order);
            }}
          >
            {order.isRejected || isFailed(order) ? "Reassign" : "Assign"}
          </button>
        );
      }
      return <span style={{ color: C.taupe, fontSize: 13 }}>Assigned</span>;
    } else {
      // Non-delivery orders
      if (order.isDelivered) return <span style={{ color: "#2f7d33", fontSize: 13 }}>Completed</span>;
      return (
        <button
          type="button"
          className="order-mini"
          style={{ background: C.green, color: "#fff" }}
          onClick={(e) => {
            e.stopPropagation();
            this.OrderFullFill(order._id);
          }}
        >
          Complete
        </button>
      );
    }
  };

  // --- presentation helpers -------------------------------------------------

  /** Which lane an order belongs in. Mirrors the status badge exactly. */
  laneOf = (order) => {
    if (order.isCancelled || order.isDelivered) return "closed";
    if (order.isDeliveryBoyAssigned) return "out";
    if (order.isOrderAccpeted) return "accepted";
    return "new";
  };

  /**
   * Stages reached, for the detail screen's fulfilment track.
   *
   * Each stored flag is read on its own, then the track is made MONOTONIC:
   * reaching any stage means every earlier stage was necessarily passed. The
   * flags do not guarantee that on their own - isOrderAccpeted (spelled that
   * way in the model) is only written when someone clicks Accept, so an order
   * can be out for delivery, or delivered, with acceptance never recorded.
   * 1,940 delivered orders are in exactly that state.
   *
   * Without this the rail drew impossible histories: step 3 ticked, step 2 not.
   * Patching one pair at a time missed the others, so the rule is applied to
   * the whole sequence instead.
   */
  stagesOf = (order) => {
    const stages = [
      { label: "Placed", done: !!order.isOrderPlaced, meta: when(placedAt(order)) },
      { label: "Accepted", done: !!order.isOrderAccpeted, meta: "Kitchen" },
    ];
    if (order.orderPlacetype === 4) {
      stages.push({
        label: "Out for delivery",
        done: !!order.isDeliveryBoyAssigned,
        meta: order.isRejected ? "reassign needed" : isFailed(order) ? "delivery failed" : "",
      });
    }
    stages.push({ label: "Delivered", done: !!order.isDelivered, meta: "" });

    // Back-fill from the furthest stage actually reached.
    let lastDone = -1;
    stages.forEach((st, i) => {
      if (st.done) lastDone = i;
    });
    return stages.map((st, i) =>
      i < lastDone && !st.done
        ? { ...st, done: true, inferred: true, meta: "not recorded" }
        : st
    );
  };

  handlePeriodChange = (period) => {
    this.setState({ period }, () => this.getALllorder(1));
  };

  runSearch = (e) => {
    if (e) e.preventDefault();
    this.getALllorder(1, this.state.search);
  };

  clearSearch = () => {
    this.setState({ search: "" }, () => this.getALllorder(1, ""));
  };

  // --- board ----------------------------------------------------------------

  renderOrderCard(order, lane) {
    const accent =
      (LANES.find((l) => l.key === lane) || {}).dot || C.brown;
    return (
      <div
        key={order._id}
        className="order-card"
        style={{ borderLeft: `4px solid ${accent}` }}
        onClick={() => this.viewOrderDetail(order)}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>{orderRef(order)}</span>
          <span style={{ fontSize: 12, color: C.taupeLight }}>{when(placedAt(order))}</span>
        </div>

        <div style={{ marginTop: 8, fontSize: 13, color: C.taupe }}>
          {order.username || "Guest"}
          {order.mobile ? ` · ${order.mobile}` : ""}
        </div>

        <div
          style={{
            marginTop: 10,
            fontSize: 14,
            color: "#3a3a3a",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          title={order.food || ""}
        >
          {order.food || "No item summary"}
        </div>

        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Chip tone="neutral">{this.getOrderTypeLabel(order.orderPlacetype)}</Chip>
          {this.getPaymentBadge(order)}
          {isFailed(order) && !order.isCancelled && !order.isDelivered && this.failedChip(order)}
        </div>

        {order.isCancelled && cancelledByLabel(order) && (
          <div style={{ marginTop: 8, fontSize: 12, color: C.red }}>
            {cancelledByLabel(order)}
            {fieldOf(order, "cancelledAt") ? ` · ${when(fieldOf(order, "cancelledAt"))}` : ""}
          </div>
        )}

        <div
          style={{
            marginTop: 13,
            paddingTop: 12,
            borderTop: "1px dashed #eee0cd",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 500, color: C.ink }}>
            {money(order.total)}
          </span>
          <span onClick={(e) => e.stopPropagation()}>{this.renderActionButton(order)}</span>
        </div>
      </div>
    );
  }

  renderBoard() {
    const { items } = this.state;
    const byLane = { new: [], accepted: [], out: [], closed: [] };
    (items || []).forEach((o) => byLane[this.laneOf(o)].push(o));

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 16,
          alignItems: "start",
        }}
      >
        {LANES.map((lane) => (
          <div key={lane.key} style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "0 4px 12px" }}>
              <span
                style={{ width: 9, height: 9, borderRadius: 5, background: lane.dot, flex: "0 0 auto" }}
              />
              <span style={{ fontSize: 15, fontWeight: 500, color: C.brownDeep }}>{lane.label}</span>
              <span style={{ fontSize: 13, color: C.taupeLight }}>{byLane[lane.key].length}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {byLane[lane.key].length === 0 ? (
                <div
                  style={{
                    border: `1px dashed ${C.border}`,
                    borderRadius: 14,
                    padding: "22px 14px",
                    textAlign: "center",
                    fontSize: 13,
                    color: C.taupeLight,
                  }}
                >
                  Nothing here
                </div>
              ) : (
                byLane[lane.key].map((o) => this.renderOrderCard(o, lane.key))
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderList() {
    const { items } = this.state;
    return (
      <div style={{ background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 18, padding: 8 }}>
        <Table
          rows={items || []}
          empty="No orders found"
          onRowClick={this.viewOrderDetail}
          columns={[
            { key: "ref", label: "Order", render: (o) => orderRef(o) },
            { key: "createdAt", label: "Placed", render: (o) => when(placedAt(o)) },
            {
              key: "customer",
              label: "Customer",
              render: (o) => (
                <div>
                  <div>{o.username || "N/A"}</div>
                  <div style={{ fontSize: 12, color: C.taupeLight }}>{o.mobile}</div>
                </div>
              ),
            },
            {
              key: "food",
              label: "Items",
              render: (o) => (
                <div
                  style={{
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={o.food || ""}
                >
                  {o.food || "N/A"}
                </div>
              ),
            },
            { key: "type", label: "Type", render: (o) => this.getOrderTypeLabel(o.orderPlacetype) },
            {
              key: "total",
              label: "Amount",
              align: "right",
              render: (o) => <strong>{money(o.total)}</strong>,
            },
            {
              key: "status",
              label: "Status",
              render: (o) => (
                <div>
                  {this.getStatusBadge(o)}
                  {o.isCancelled && cancelledByLabel(o) && (
                    <div style={{ marginTop: 4, fontSize: 12, color: C.taupeLight }}>
                      {cancelledByLabel(o).replace("Cancelled ", "")}
                      {fieldOf(o, "cancelledAt") ? ` · ${when(fieldOf(o, "cancelledAt"))}` : ""}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "action",
              label: "Action",
              render: (o) => (
                <span onClick={(e) => e.stopPropagation()}>{this.renderActionButton(o)}</span>
              ),
            },
          ]}
        />
      </div>
    );
  }

  renderPagination() {
    const { currentPage, totalPages, limit, totalCount } = this.state;
    if (totalPages <= 1) return null;

    const pages = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
      if (totalPages <= 5) return i + 1;
      if (currentPage <= 3) return i + 1;
      if (currentPage >= totalPages - 2) return totalPages - 4 + i;
      return currentPage - 2 + i;
    });

    return (
      <div
        style={{
          marginTop: 22,
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: 13, color: C.taupe }}>
          Page {currentPage} of {totalPages} · {totalCount} orders
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
          <button
            type="button"
            className="order-page"
            disabled={currentPage <= 1}
            onClick={() => this.getALllorder(currentPage - 1)}
          >
            Prev
          </button>
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              className={`order-page${currentPage === p ? " is-active" : ""}`}
              onClick={() => this.getALllorder(p)}
            >
              {p}
            </button>
          ))}
          <button
            type="button"
            className="order-page"
            disabled={currentPage >= totalPages}
            onClick={() => this.getALllorder(currentPage + 1)}
          >
            Next
          </button>
          <Input
            type="select"
            value={limit}
            onChange={(e) => {
              this.setState({ limit: parseInt(e.target.value) }, () => {
                this.getALllorder(1);
              });
            }}
            style={{
              width: 74,
              height: 32,
              borderRadius: 16,
              border: `1px solid ${C.border}`,
              fontSize: 13,
              padding: "0 8px",
            }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </Input>
        </div>
      </div>
    );
  }

  // --- order detail ---------------------------------------------------------

  renderStageTrack(order) {
    const stages = this.stagesOf(order);
    return (
      <div style={{ display: "flex", gap: 8 }}>
        {stages.map((s, i) => (
          <div key={s.label} style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                height: 4,
                borderRadius: 2,
                background: s.done ? C.brown : C.tint,
              }}
            />
            <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 11,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  background: s.done ? C.brown : "#f7f0e4",
                  border: s.done ? "none" : `2px solid ${C.border}`,
                  color: s.done ? "#fff" : C.taupeLight,
                }}
              >
                {/* The tick is a Material Symbols ligature and needs the .ms
                    font; the step NUMBER is just a digit and must not use it,
                    or it renders in the icon face and looks mismatched. */}
                {s.done ? (
                  <span className="ms" style={{ fontSize: 14 }}>check</span>
                ) : (
                  <span style={{ fontSize: 12, fontWeight: 500, lineHeight: 1 }}>
                    {i + 1}
                  </span>
                )}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: s.done ? C.ink : C.taupeLight }}>{s.label}</div>
                {s.meta ? (
                  <div style={{ fontSize: 12, color: C.taupeLight }}>{s.meta}</div>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  /**
   * Recorded status changes, oldest first. Unlike the stage track above it this
   * is a log, not a summary: it keeps the real time of each step, plus the
   * events the track has no slot for (paid, declined, cancelled), and it never
   * infers a step that was not written. Orders older than the history get a
   * lone "Placed" entry from createdAt.
   */
  historyOf = (order) => {
    const history = Array.isArray(rawOf(order).statusHistory) ? rawOf(order).statusHistory : [];
    const entries = history
      .filter((h) => h && h.status)
      .map((h, i) => ({ status: h.status, at: h.at, i }))
      .sort((a, b) => {
        const ta = new Date(a.at).getTime() || 0;
        const tb = new Date(b.at).getTime() || 0;
        return ta - tb || a.i - b.i;
      });
    if (entries.length) return { entries, legacy: false };
    const at = placedAt(order);
    return { entries: at ? [{ status: "placed", at, i: 0 }] : [], legacy: true };
  };

  renderTimeline(order) {
    const { entries, legacy } = this.historyOf(order);
    if (!entries.length) return null;
    return (
      <div style={{ background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>Status history</div>
        <div style={{ marginTop: 4, marginBottom: 16, fontSize: 13, color: C.taupeLight }}>
          {legacy ? "This order predates the status log" : "Every recorded change, oldest first"}
        </div>
        {entries.map((e, idx) => {
          const isLast = idx === entries.length - 1;
          const bad = BAD_HISTORY.includes(e.status);
          return (
            <div key={`${e.status}-${e.i}`} style={{ display: "flex", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 12px" }}>
                <span
                  style={{
                    width: 12, height: 12, borderRadius: 6, marginTop: 3,
                    background: bad ? C.red : isLast ? C.brown : C.sand,
                  }}
                />
                {!isLast && <span style={{ flex: 1, width: 2, minHeight: 18, background: C.tint }} />}
              </div>
              <div style={{ paddingBottom: isLast ? 0 : 14, minWidth: 0 }}>
                <div style={{ fontSize: 14, color: bad ? C.red : C.ink }}>
                  {HISTORY_LABELS[e.status] || e.status}
                </div>
                <div style={{ fontSize: 12, color: C.taupeLight }}>{when(e.at) || "Time not recorded"}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /**
   * Each runner's part in the order (OrderFood.driverLog), oldest first: who
   * declined it, couldn't deliver it, had it taken off them, or held it when it
   * was cancelled. Names come from the loaded runner list; a runner no longer
   * in it is shown without one.
   */
  renderRunnerHistory(order) {
    const log = Array.isArray(fieldOf(order, "driverLog")) ? fieldOf(order, "driverLog") : [];
    const entries = log
      .filter((l) => l && l.event)
      .map((l, i) => ({ ...l, i }))
      .sort((a, b) => (new Date(a.at).getTime() || 0) - (new Date(b.at).getTime() || 0) || a.i - b.i);
    if (!entries.length) return null;
    return (
      <div style={{ background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 18, padding: 20 }}>
        <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>Runner history</div>
        <div style={{ marginTop: 4, marginBottom: 12, fontSize: 13, color: C.taupeLight }}>
          Runners this order passed through
        </div>
        {entries.map((l, idx) => {
          const name = this.runnerName(l.driverId);
          const reason = l.event === "failed" ? failedReasonLabel(l.reason) : l.reason;
          return (
            <div
              key={`${l.event}-${l.i}`}
              style={{
                padding: "9px 0",
                borderTop: idx === 0 ? "none" : `1px solid ${C.panel}`,
                display: "flex",
                gap: 10,
                alignItems: "baseline",
                justifyContent: "space-between",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, color: l.event === "failed" ? C.red : C.ink }}>
                  {DRIVER_EVENTS[l.event] || l.event}
                  {name ? <span style={{ color: C.taupe }}> · {name}</span> : null}
                </div>
                {reason ? (
                  <div style={{ marginTop: 2, fontSize: 12, color: C.slate, overflowWrap: "anywhere" }}>
                    {reason}
                  </div>
                ) : null}
              </div>
              <div style={{ fontSize: 12, color: C.taupeLight, whiteSpace: "nowrap" }}>
                {when(l.at) || "Time not recorded"}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /** Why the last runner couldn't deliver, in the runner's words. */
  renderFailureNotice(order) {
    if (!isFailed(order)) return null;
    const note = fieldOf(order, "deliveryFailedNote");
    const at = fieldOf(order, "deliveryFailedAt");
    const runner = this.runnerName(fieldOf(order, "DeliveryBoyId"));
    const live = !order.isCancelled && !order.isDelivered;
    return (
      <div
        style={{
          padding: "16px 18px",
          borderRadius: 18,
          background: "rgba(192,32,43,.08)",
          border: "1px solid rgba(192,32,43,.18)",
          color: C.red,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="ms" style={{ fontSize: 20 }}>report</span>
          <span style={{ fontSize: 16, fontWeight: 500 }}>
            Delivery failed: {failedReasonLabel(fieldOf(order, "deliveryFailedReason"))}
          </span>
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: C.slate }}>
          {runner ? `Reported by ${runner}` : "Reported by the runner"}
          {at ? ` · ${when(at)}` : ""}
        </div>
        {note ? (
          <div style={{ marginTop: 8, fontSize: 14, color: C.ink, lineHeight: 1.45, overflowWrap: "anywhere" }}>
            “{note}”
          </div>
        ) : null}
        {live && (
          <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Btn
              icon="delivery_dining"
              onClick={() => {
                this.closeOrderDetail();
                this.OrderManagement(order);
              }}
            >
              Reassign
            </Btn>
            <Btn kind="quiet" icon="cancel" style={{ color: C.red }} onClick={() => this.openCancel(order)}>
              Cancel order
            </Btn>
          </div>
        )}
      </div>
    );
  }

  renderItemsPanel(order) {
    const items = order.foodItems || [];
    return (
      <div
        style={{
          background: "#fff",
          border: `1px solid ${C.tint}`,
          borderRadius: 18,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${C.panel}`,
            fontSize: 17,
            fontWeight: 500,
            color: C.ink,
          }}
        >
          Items · {items.length}
        </div>

        <div>
          {items.length === 0 && (
            <div style={{ padding: "22px 20px", color: C.taupeLight, fontSize: 14 }}>
              This order has no line items.
            </div>
          )}
          {items.map((item, index) => {
            // Per-unit price (after item discount)
            const unitPrice =
              item.foodId?.finalAmount || (item.finalAmount / (item.quantity || 1)) || 0;
            // Item total from stored cartTotal (includes add-ons) or calculated
            const itemTotal = item.cartTotal || unitPrice * (item.quantity || 1);
            return (
              <React.Fragment key={index}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "13px 20px",
                    borderBottom: `1px solid ${C.panel}`,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, color: C.ink }}>
                      {item.foodName || item.foodId?.foodName || "Food Item"}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: C.taupe }}>× {item.quantity || 1}</div>
                  <div style={{ width: 80, textAlign: "right", fontSize: 13, color: C.taupeLight }}>
                    {money(unitPrice)}
                  </div>
                  <div style={{ width: 90, textAlign: "right", fontSize: 15, fontWeight: 500, color: C.ink }}>
                    {money(itemTotal)}
                  </div>
                </div>
                {item.addOns &&
                  item.addOns.length > 0 &&
                  item.addOns.map((addOn, addOnIndex) => (
                    <div
                      key={`addon-${index}-${addOnIndex}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        padding: "8px 20px 8px 40px",
                        background: C.surface,
                        borderBottom: `1px solid ${C.panel}`,
                        fontSize: 12,
                        color: C.taupe,
                      }}
                    >
                      <div style={{ flex: 1, fontStyle: "italic" }}>
                        + {addOn.foodAddOnName || "Add-on"}
                      </div>
                      <div>× {addOn.value || 1}</div>
                      <div style={{ width: 80, textAlign: "right" }}>
                        {money(addOn.foodAddOnAmount)}
                      </div>
                      <div style={{ width: 90, textAlign: "right" }}>
                        +{money((addOn.foodAddOnAmount || 0) * (addOn.value || 1))}
                      </div>
                    </div>
                  ))}
              </React.Fragment>
            );
          })}

          {order.foodAddOn && order.foodAddOn.length > 0 && (
            <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.panel}` }}>
              <div style={{ fontSize: 12, color: C.taupeLight, marginBottom: 6 }}>
                Order add-ons
              </div>
              {order.foodAddOn.map((addOn, index) => (
                <div
                  key={index}
                  style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.slate }}
                >
                  <span>
                    {addOn.foodAddOns?.foodAddOnName || "Add-on"} ×{addOn.value || 1}
                  </span>
                  <span>+{money((addOn.foodAddOns?.amount || 0) * (addOn.value || 1))}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {this.renderTotals(order)}
      </div>
    );
  }

  renderTotals(order) {
    // Stored subtotal (includes add-ons) or calculated from cartTotal
    let subtotal = order.subtotal || 0;

    if (!subtotal) {
      subtotal = (order.foodItems || []).reduce((sum, item) => {
        return sum + (item.cartTotal || 0);
      }, 0);
    }

    // Fallback: unit prices (without add-ons) for very old orders
    if (!subtotal) {
      subtotal = (order.foodItems || []).reduce((sum, item) => {
        const unitPrice =
          item.foodId?.finalAmount || (item.finalAmount / (item.quantity || 1)) || 0;
        return sum + unitPrice * (item.quantity || 1);
      }, 0);
    }

    const couponDiscount = order.couponDiscount || 0;
    const total =
      order.total !== undefined && order.total !== null ? order.total : subtotal - couponDiscount;

    // Branch charges, already inside the stored total. Only on the raw record.
    const raw = rawOf(order);
    const deliveryCharge = Number(order.deliveryCharge || raw.deliveryCharge) || 0;
    const taxAmount = Number(order.taxAmount || raw.taxAmount) || 0;
    const taxRate = Number(order.taxRate || raw.taxRate) || 0;
    const pointsRedeemed = Number(order.pointsRedeemed || raw.pointsRedeemed) || 0;
    const pointsEarned = Number(order.pointsEarned || raw.pointsEarned) || 0;

    const line = (l, r, tone) => (
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, color: C.slate }}>
        <span>{l}</span>
        <span style={{ color: tone || C.ink }}>{r}</span>
      </div>
    );

    return (
      <div
        style={{
          padding: "16px 20px",
          background: C.surface,
          display: "flex",
          flexDirection: "column",
          gap: 9,
        }}
      >
        {line("Subtotal", money(subtotal))}
        {order.couponCode
          ? line(`Coupon ${order.couponCode}`, `− ${money(couponDiscount)}`, C.green)
          : null}
        {deliveryCharge > 0 ? line("Delivery charge", money(deliveryCharge)) : null}
        {taxAmount > 0 ? line(taxRate > 0 ? `Tax (${taxRate}%)` : "Tax", money(taxAmount)) : null}
        {line(
          "Payment",
          order.paymentMethodType === 1 || order.paymentMethodType === "Cash" ? "Cash" : "Online"
        )}
        {pointsRedeemed > 0 ? line("Points redeemed", pts(pointsRedeemed)) : null}
        {pointsEarned > 0 ? line("Points earned", `+${pts(pointsEarned)}`) : null}
        <div
          style={{
            marginTop: 4,
            paddingTop: 11,
            borderTop: "1px solid #eee0cd",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span style={{ fontSize: 16, color: C.ink }}>Total</span>
          <span style={{ fontSize: 22, fontWeight: 500, color: C.ink }}>{money(total)}</span>
        </div>
      </div>
    );
  }

  renderGuestPanel(order) {
    const initials = (order.username || "G")
      .split(" ")
      .map((p) => p.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();

    const cell = (k, v) => (
      <div style={{ padding: "11px 13px", borderRadius: 12, background: C.surface, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.taupeLight }}>{k}</div>
        <div
          style={{ marginTop: 4, fontSize: 14, color: C.ink, overflowWrap: "anywhere" }}
        >
          {v || "—"}
        </div>
      </div>
    );

    return (
      <div style={{ background: "#fff", border: `1px solid ${C.tint}`, borderRadius: 18, padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
          <span
            style={{
              width: 46,
              height: 46,
              borderRadius: 23,
              background: C.iconTint,
              color: C.brown,
              display: "grid",
              placeItems: "center",
              fontSize: 16,
              fontWeight: 700,
              flex: "0 0 auto",
            }}
          >
            {initials}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, color: C.ink }}>{order.username || "Guest"}</div>
            <div style={{ marginTop: 3, fontSize: 13, color: C.taupeLight, overflowWrap: "anywhere" }}>
              {order.email || "No email on file"}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {cell("Phone", order.mobile)}
          {cell("Order type", this.getOrderTypeLabel(order.orderPlacetype))}
        </div>

        {/* Only for Home Delivery (orderPlacetype 4). 3,765 dine-in, pickup and
            room-service orders carry a stray deliveryAddress - often just "1" -
            and showing it implied a delivery that never existed. */}
        {order.orderPlacetype === 4 && order.deliveryAddress && (
          <div style={{ marginTop: 10 }}>{cell("Delivery address", order.deliveryAddress)}</div>
        )}

        {/* Who has it now. A declined order keeps the decliner's id, so it is
            only shown while a runner actually holds the order. */}
        {order.orderPlacetype === 4 &&
          order.isDeliveryBoyAssigned &&
          !order.isRejected &&
          !isFailed(order) &&
          fieldOf(order, "DeliveryBoyId") && (
            <div style={{ marginTop: 10 }}>
              {cell("Runner", this.runnerName(fieldOf(order, "DeliveryBoyId")) || "Assigned")}
            </div>
          )}

        {order.instructions && (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: 12,
              border: `1px dashed ${C.border}`,
              fontSize: 13,
              color: C.slate,
              lineHeight: 1.45,
            }}
          >
            <span style={{ color: C.taupeLight }}>Guest note · </span>
            {order.instructions}
          </div>
        )}

        {order.isCancelled && (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(192,32,43,.08)",
              color: C.red,
              fontSize: 13,
            }}
          >
            <strong>
              {cancelledByLabel(order) || "Cancelled"}
              {fieldOf(order, "cancelledAt") ? ` · ${when(fieldOf(order, "cancelledAt"))}` : ""}.
            </strong>{" "}
            {order.cancellationReason || "No reason provided"}
          </div>
        )}
      </div>
    );
  }

  renderDetailModal() {
    const { selectedOrder } = this.state;

    return (
      <Modal
        isOpen={this.state.showOrderDetail}
        toggle={this.closeOrderDetail}
        size="xl"
        scrollable
        className="con-modal order-modal"
      >
        {selectedOrder && (
          <div>
            {/* Topbar, mirroring the detail screen's header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "18px 24px",
                borderBottom: `1px solid ${C.border}`,
                background: "#fbf3e7",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={this.closeOrderDetail}
                aria-label="Back to orders"
                style={{
                  border: `1px solid ${C.border}`,
                  background: "#fff",
                  color: C.brown,
                  padding: 9,
                  borderRadius: 12,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                <span className="ms" style={{ fontSize: 22 }}>arrow_back</span>
              </button>
              <div>
                <div style={{ fontSize: 12, color: C.taupeLight, letterSpacing: ".6px" }}>
                  FOOD / ORDERS / {orderRef(selectedOrder)}
                </div>
                <div style={{ marginTop: 3, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 22, fontWeight: 500, color: C.brownDeep }}>
                    Order {orderRef(selectedOrder)}
                  </span>
                  {this.getStatusBadge(selectedOrder)}
                  {this.getPaymentBadge(selectedOrder)}
                  <span style={{ fontSize: 13, color: C.taupeLight }}>
                    Placed {when(placedAt(selectedOrder))} ·{" "}
                    {this.getOrderTypeLabel(selectedOrder.orderPlacetype)}
                  </span>
                </div>
              </div>

              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
                {!selectedOrder.isCancelled && !selectedOrder.isDelivered && (
                  <React.Fragment>
                    {!selectedOrder.isPaymentSuccess && (
                      <Btn
                        kind="quiet"
                        icon="payments"
                        onClick={() => {
                          this.StatusChangeRequest(selectedOrder._id, false);
                          this.closeOrderDetail();
                        }}
                      >
                        Mark Paid
                      </Btn>
                    )}
                    {selectedOrder.orderPlacetype === 4 &&
                      (!selectedOrder.isDeliveryBoyAssigned || isFailed(selectedOrder)) && (
                        <Btn
                          icon="delivery_dining"
                          onClick={() => {
                            this.closeOrderDetail();
                            this.OrderManagement(selectedOrder);
                          }}
                        >
                          {selectedOrder.isRejected || isFailed(selectedOrder) ? "Reassign" : "Assign Delivery"}
                        </Btn>
                      )}
                    {/* Still with a runner: the hotel may take it off them.
                        ReAssignDeliveryBoy needs a placed order. */}
                    {selectedOrder.orderPlacetype === 4 &&
                      selectedOrder.isDeliveryBoyAssigned &&
                      !isFailed(selectedOrder) &&
                      selectedOrder.isOrderPlaced && (
                        <Btn
                          kind="quiet"
                          icon="swap_horiz"
                          onClick={() => {
                            this.closeOrderDetail();
                            this.OrderManagement(selectedOrder);
                          }}
                        >
                          Reassign
                        </Btn>
                      )}
                    {selectedOrder.orderPlacetype !== 4 && (
                      <Btn
                        icon="check"
                        style={{ background: C.green }}
                        onClick={() => {
                          this.OrderFullFill(selectedOrder._id);
                          this.closeOrderDetail();
                        }}
                      >
                        Complete Order
                      </Btn>
                    )}
                  </React.Fragment>
                )}
                {canCancel(selectedOrder) && (
                  <Btn
                    kind="quiet"
                    icon="cancel"
                    style={{ color: C.red }}
                    onClick={() => this.openCancel(selectedOrder)}
                  >
                    Cancel order
                  </Btn>
                )}
                <Btn kind="quiet" onClick={this.closeOrderDetail}>
                  Close
                </Btn>
              </div>
            </div>

            <div
              style={{
                padding: 24,
                background: C.canvas,
                display: "grid",
                gridTemplateColumns: "1.6fr 1fr",
                gap: 20,
                alignItems: "start",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
                {this.renderFailureNotice(selectedOrder)}
                <div
                  style={{
                    background: "#fff",
                    border: `1px solid ${C.tint}`,
                    borderRadius: 18,
                    padding: 22,
                  }}
                >
                  <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>Fulfilment stage</div>
                  <div style={{ marginTop: 4, marginBottom: 20, fontSize: 13, color: C.taupeLight }}>
                    Advancing a stage notifies the guest app
                  </div>
                  {this.renderStageTrack(selectedOrder)}
                </div>

                {this.renderItemsPanel(selectedOrder)}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
                {this.renderGuestPanel(selectedOrder)}
                {this.renderTimeline(selectedOrder)}
                {this.renderRunnerHistory(selectedOrder)}
              </div>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  /**
   * What the picker says about the order's current runner, and which option
   * the server would refuse: the runner who declined or failed it, or the one
   * already holding it. Busy runners are never blocked.
   */
  assignContextOf(r) {
    const prev = idOf(r && r.DeliveryBoyId);
    if (!r || !prev) return { prev: "", note: "", why: "" };
    const name = this.runnerName(prev) || "The previous runner";
    if (r.isDeliveryFailed) {
      return { prev, why: "couldn't deliver this", note: `${name} couldn't deliver it. Pick a runner to try again.` };
    }
    if (r.isRejected) {
      return { prev, why: "declined this", note: `${name} declined it.` };
    }
    if (r.isDeliveryBoyAssigned) {
      return {
        prev,
        why: "has this order",
        note: `It is with ${name} now. They are told it was taken off them; the new runner accepts it again.`,
      };
    }
    return { prev: "", note: "", why: "" };
  }

  renderAssignModal() {
    const { assignMode, assignOrder, Dboy } = this.state;
    const reassign = assignMode === "reassign";
    const ctx = this.assignContextOf(assignOrder);
    const picked = (this.state.DBoyData || []).find((d) => String(d._id) === String(Dboy));
    const carrying = (picked && Array.isArray(picked.activeOrders) && picked.activeOrders) || [];
    return (
      <Modal isOpen={this.state.modal} toggle={this.toggle} className="con-modal order-modal">
        <div style={{ padding: 30 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 500, color: C.ink }}>
                {reassign ? "Reassign this order" : "Assign a runner"}
              </div>
              <div style={{ marginTop: 4, fontSize: 14, color: C.taupe }}>
                {ctx.note || "The order moves to “out for delivery” once assigned"}
              </div>
            </div>
            <button
              type="button"
              onClick={this.toggle}
              aria-label="Close"
              style={{
                border: 0,
                background: "transparent",
                color: C.taupeLight,
                cursor: "pointer",
                padding: 4,
                lineHeight: 1,
              }}
            >
              <span className="ms" style={{ fontSize: 22 }}>close</span>
            </button>
          </div>

          <AvForm onValidSubmit={this.handleSubmit}>
            <div style={{ marginTop: 22 }}>
              <div
                style={{
                  fontSize: 12,
                  letterSpacing: ".6px",
                  color: C.taupe,
                  marginBottom: 6,
                  textTransform: "uppercase",
                }}
              >
                Delivery boy
              </div>
              <Input
                type="select"
                name="Dboy"
                id="Dboy"
                value={this.state.Dboy}
                onChange={this.handleChange}
                required
              >
                <option value="">Select Delivery Boy</option>
                {(this.state.DBoyData || []).map((e, key) => {
                  const refused = ctx.prev && String(e._id) === ctx.prev;
                  return (
                    <option key={key} value={e._id} disabled={refused}>
                      {this.runnerLabel(e)}
                      {refused ? ` · ${ctx.why}` : ""}
                    </option>
                  );
                })}
              </Input>
              {carrying.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 13, color: C.taupe }}>
                  Already carrying{" "}
                  {carrying
                    .map(
                      (a) =>
                        `${orderRef(a)} (${
                          a.OutforDelivery ? "out" : a.isOrderAccpeted ? "accepted" : "not accepted yet"
                        })`
                    )
                    .join(", ")}
                </div>
              )}
            </div>

            {reassign && (
              <div style={{ marginTop: 18 }}>
                <div
                  style={{
                    fontSize: 12,
                    letterSpacing: ".6px",
                    color: C.taupe,
                    marginBottom: 6,
                    textTransform: "uppercase",
                  }}
                >
                  Reason (optional)
                </div>
                <textarea
                  id="reassignReason"
                  value={this.state.reassignReason}
                  onChange={this.handleChange}
                  maxLength={500}
                  placeholder="e.g. Closer runner available"
                  style={reasonStyle}
                />
              </div>
            )}

            <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={this.toggle}
                style={{
                  flex: 1,
                  height: 52,
                  border: "1px solid #e6d8c4",
                  borderRadius: 26,
                  background: "#fff",
                  color: C.slate,
                  fontFamily: "inherit",
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  flex: 1,
                  height: 52,
                  border: 0,
                  borderRadius: 26,
                  background: C.brown,
                  color: "#fff",
                  fontFamily: "inherit",
                  fontSize: 15,
                  cursor: "pointer",
                }}
              >
                {reassign ? "Reassign" : "Assign"}
              </button>
            </div>
          </AvForm>
        </div>
      </Modal>
    );
  }

  renderCancelDialog() {
    const { cancelTarget, cancelReason, cancelling } = this.state;
    const o = cancelTarget;
    const held = o && o.orderPlacetype === 4 && o.isDeliveryBoyAssigned && !o.isRejected && !isFailed(o);
    return (
      <ConfirmDialog
        isOpen={!!o}
        toggle={this.closeCancel}
        icon="cancel"
        title={o ? `Cancel order ${orderRef(o)}?` : "Cancel order?"}
        body={
          <div>
            <div>
              {o && o.username ? `${o.username} is told` : "The guest is told"}
              {held ? ", and so is the runner carrying it." : "."} This can't be undone.
            </div>
            <div style={{ marginTop: 16, textAlign: "left" }}>
              <div style={{ fontSize: 12, letterSpacing: ".6px", color: C.taupe, marginBottom: 6, textTransform: "uppercase" }}>
                Reason (optional)
              </div>
              <textarea
                value={cancelReason}
                onChange={(e) => this.setState({ cancelReason: e.target.value })}
                maxLength={500}
                placeholder="Shown to the guest. Defaults to “Cancelled by the hotel”."
                style={reasonStyle}
              />
            </div>
          </div>
        }
        tip={
          o && o.isPaymentSuccess
            ? "This order is marked paid. Cancelling doesn't refund it; settle the cash with the guest."
            : null
        }
        confirmText="Cancel order"
        cancelText="Keep order"
        onConfirm={this.confirmCancel}
        loading={cancelling}
      />
    );
  }

  render() {
    const { items, isLoaded, totalCount, search, view } = this.state;

    return (
      <ConsolePage
        title="Live order board"
        subtitle={
          isLoaded
            ? `${totalCount} order${totalCount === 1 ? "" : "s"} · ${(items || []).length} on this page`
            : "Loading orders…"
        }
        actions={
          <React.Fragment>
            <NavPills
              active="/FoodOrder"
              items={[
                { to: "/Food", label: "Menu & items" },
                { to: "/FoodOrder", label: "Orders" },
                { to: "/Foodcuisine", label: "Cuisines" },
              ]}
            />
            <form onSubmit={this.runSearch} style={{ margin: 0 }}>
              <SearchPill
                value={search}
                onChange={(v) => this.setState({ search: v })}
                placeholder="Order ID or guest"
                width={220}
              />
            </form>
            <Btn kind="quiet" icon="search" onClick={this.runSearch}>
              Search
            </Btn>
            <PillToggle
              value={view}
              onChange={(v) => this.setState({ view: v })}
              options={[
                { value: "board", label: "Board" },
                { value: "list", label: "List" },
              ]}
            />
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <PeriodFilter value={this.state.period} onChange={this.handlePeriodChange} style={{ marginBottom: 16 }} />

        {search.trim() !== "" && (
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
            <Chip tone="neutral">
              <span className="ms" style={{ fontSize: 16 }}>search</span>
              {search}
            </Chip>
            <button
              type="button"
              onClick={this.clearSearch}
              style={{
                border: 0,
                background: "transparent",
                color: C.taupe,
                fontSize: 14,
                fontFamily: "inherit",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        )}

        {!isLoaded ? (
          <div style={{ padding: "70px 0", textAlign: "center", color: C.taupeLight }}>
            Loading orders…
          </div>
        ) : (items || []).length === 0 ? (
          <div
            style={{
              padding: "70px 0",
              textAlign: "center",
              color: C.taupeLight,
              background: "#fff",
              border: `1px solid ${C.tint}`,
              borderRadius: 18,
            }}
          >
            <span className="ms" style={{ fontSize: 38, display: "block", marginBottom: 10 }}>
              inbox
            </span>
            No orders found
          </div>
        ) : view === "board" ? (
          this.renderBoard()
        ) : (
          this.renderList()
        )}

        {this.renderPagination()}

        {this.renderAssignModal()}
        {this.renderDetailModal()}
        {this.renderCancelDialog()}
      </ConsolePage>
    );
  }
}

export default FoodOrder;
